// Styles
import React from 'react';
import styles from './changeCustomPatchesCodesModal.module.css';
import { addAuthorizationHeader } from '../../../api/network';
import { getBackendApiUrl } from '../../../config';


interface ChangeCustomPatchesCodesProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (comment?: string) => void;
  wizardData: Record<string, any>;
  docId?: string;
}

const ChangeCustomPatchesCodesModal: React.FC<ChangeCustomPatchesCodesProps> = ({
  isOpen,
  onClose,
  onSuccess,
  wizardData,
  docId,
}) => {
  if (!isOpen) return null;
  
  //wizardData has the following properties:
  //- customPatchesThreadColorNumber: the number of thread colours, it's a string that can be parsed into an integer that ranges from 1 to 12
  //- for each thread colour there is a string property named `customPatchesThreadColorXThread` where X is the thread colour number (1 to 12)
  //- optionally there might be a property named `customPatchesBackgroundColorThread`
  // wizardData also has a bunch of other properties we don't need to include in the thread codes update
  //we want a component all the mentioned properties (except for the number of thread colours) as input fields for the user to change them
  //for the style and the layout we want to follow the same pattern as ./swapCustomCutLayersModal.tsx

  const [processing, setProcessing] = React.useState<boolean>(false);
  const [currentWizardData, setCurrentWizardData] = React.useState<Record<string, any>>(wizardData);
  const [invalidThreadCodes, setInvalidThreadCodes] = React.useState<boolean>(false);

  React.useEffect(() => {
    const threadColorCount = parseInt(wizardData.customPatchesThreadColorNumber || '0', 10);
    for (let i = 1; i <= threadColorCount; i++) {
      if (!currentWizardData[`customPatchesThreadColor${i}Thread`]) {
        setInvalidThreadCodes(true);
        return;
      }
    }

    if(wizardData.customPatchesBackgroundColorThread && !currentWizardData.customPatchesBackgroundColorThread) {
      setInvalidThreadCodes(true);
      return;
    }
    
    setInvalidThreadCodes(false);
  }, [currentWizardData]);

  function CloseIcon() {
    return (
      <button type="button" className={`${styles.closeIcon} bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500`} disabled={processing}>
        <span className="sr-only">Close menu</span>
        <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    );
  }

  const saveCodes = () => {
    setProcessing(true);

    const headers = addAuthorizationHeader({
      'Content-Type': 'application/json'
    });

    const newThreadCodes:Record<string, string> = {}
    const threadColorCount = parseInt(wizardData.customPatchesThreadColorNumber || '0', 10);
    for (let i = 1; i <= threadColorCount; i++) {
      newThreadCodes[`customPatchesThreadColor${i}Thread`] = currentWizardData[`customPatchesThreadColor${i}Thread`] || '';
    }
    if (currentWizardData.customPatchesBackgroundColorThread) {
      newThreadCodes.customPatchesBackgroundColorThread = currentWizardData.customPatchesBackgroundColorThread;
    }

    fetch(`${getBackendApiUrl()}/design-custom-patches-thread-codes`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({
        id: docId,
        threadCodes: newThreadCodes,
      })
    }).then(async (processResp) => {
      if (!processResp.ok) throw new Error("Error changing custom patches codes " + await processResp.text())
      setProcessing(false);
      onSuccess();
    }).catch(err => {
      console.log("Failed changing custom patches codes: " + (err as Error).message)
      setProcessing(false);
    });
  };


  return (
    <div className={styles.modalOverlay}>
      <div className={`${styles.modalContainer} relative w-auto my-auto mx-auto max-w-3xl`}>
        <div className={`${styles.modalWrapper} border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none`}>
          <div className={styles.header}>
            <span>Edit Custom Patches Codes</span>
            <div className={styles.closeIcon} onClick={processing ? undefined : onClose}>
              <CloseIcon />
            </div>
          </div>
          {(
              <>
                <div className={styles.modalContent}>
                  {/*
                    Render input fields for each thread color and the background color if present
                  */}
                  {Array.from({ length: parseInt(currentWizardData.customPatchesThreadColorNumber || '0', 10) }, (_, i) => (
                    <div key={i} className="mb-4">
                      <label className="block text-gray-700 text-sm font-bold mb-2">
                        Thread Color {i + 1}
                      </label>
                      <input
                        type="text"
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        value={currentWizardData[`customPatchesThreadColor${i + 1}Thread`] || ''}
                        disabled={processing}
                        onChange={(e) => setCurrentWizardData({
                          ...currentWizardData,
                          [`customPatchesThreadColor${i + 1}Thread`]: e.target.value
                        })}
                      />
                    </div>
                  ))}
                  {currentWizardData.customPatchesBackgroundColorThread && (
                    <div className="mb-4">
                      <label className="block text-gray-700 text-sm font-bold mb-2">
                        Background Color
                      </label>
                      <input
                        type="text"
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        value={currentWizardData.customPatchesBackgroundColorThread}
                        disabled={processing}
                        onChange={(e) => setCurrentWizardData({
                          ...currentWizardData,
                          customPatchesBackgroundColorThread: e.target.value
                        })}
                      />
                    </div>
                  )}
                </div>
                <div className={`${styles.footerButtons} p-6 border-t border-solid border-blueGray-200 rounded-b`}>
                  <button className={styles.acceptBtn} onClick={saveCodes} disabled={processing || invalidThreadCodes}>Save Codes</button>
                  <button className={styles.cancelBtn} onClick={onClose} disabled={processing}>Cancel</button>
                </div>
              </>
            )
          }
        </div>
      </div>
    </div>
  );
}

export default ChangeCustomPatchesCodesModal;